import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { RequireAccess, RequireRouteAccess } from "@/app/auth/RequireAccess";
import AccountVerificationAdminPage from "@/modules/verification/pages/AccountVerificationAdminPage";
import OnboardingVerificationPage from "@/modules/verification/pages/OnboardingVerificationPage";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";

import { AccessNavigation } from "./access/AccessNavigation";
import { AccountsWalletsPage } from "./accountsWallets/pages/AccountsWalletsPage";
import { AccountsWalletsTrackingPage } from "./accountsWallets/pages/AccountsWalletsTrackingPage";
import { AuditoriaNavigation } from "./auditoria/AuditoriaNavigation";
import { BrebQrPayNavigation } from "./breb-qr-pay/BrebQrPayNavigation";
import CardWalletsPage from "./card-wallets/pages/CardWalletsPage";
import { CardsNavigation } from "./cards/CardsNavigation";
import { CardStatementPage } from "./cards/pages/CardStatementPage";
import { ClientsNavigation } from "./clients/ClientsNavigation";
import { RoutesCountriesPage } from "./comingSoon/pages/RoutesCountriesPage";
import { DashboardLandingRedirect } from "./components/DashboardLandingRedirect";
import { ConfirmationsNavigation } from "./confirmations/ConfirmationsNavigation";
import { CryptoSwapsNavigation } from "./crypto-swaps/CryptoSwapsNavigation";
import { DispersionsNavigation } from "./dispersions/DispersionsNavigation";
import { DashboardLayout } from "./layouts/DashboardLayout";
import { NewsPage } from "./news/pages/NewsPage";
import { OrganizationsNavigation } from "./organizations/OrganizationsNavigation";
import { PayinsNavigation } from "./payins/PayinsNavigation";
import { PaymentsActivityNavigation } from "./payments-activity/PaymentsActivityNavigation";
import { PaymentsConfigNavigation } from "./payments-config/PaymentsConfigNavigation";
import { PaymentsLedgerNavigation } from "./payments-ledger/PaymentsLedgerNavigation";
import { PaymentsNavigation } from "./payments/PaymentsNavigation";
import { PayoutBatchesNavigation } from "./payout-batches/PayoutBatchesNavigation";
import { PayoutRescueNavigation } from "./payout-rescue/PayoutRescueNavigation";
import { ProfileNavigation } from "./profile/ProfileNavigation";
import { QuoterColocaNavigation } from "./quoter-coloca/QuoterColocaNavigation";
import { QuoterNavigation } from "./quoter/QuoterNavigation";
import { SettingsPage } from "./settings/pages/SettingsPage";
import { SwapsNavigation } from "./swaps/SwapsNavigation";
import { UsersNavigation } from "./users/UsersNavigation";
import { WalletTopUpsNavigation } from "./wallet-top-ups/WalletTopUpsNavigation";
import { WalletsNavigation } from "./wallets/WalletsNavigation";

// Staff-only and the one consumer of the charts library, so it stays out of the bundle every user downloads.
const MetricsNavigation = lazy(() =>
  import("./metrics/MetricsNavigation").then((module) => ({ default: module.MetricsNavigation })),
);

/**
 * Every guarded route wraps its element in `<RequireAccess to="…">`, where `to` is the entry's key in
 * `@/app/auth/navigation.registry`. The sidebar resolves the SAME registry, so a menu item and its route are
 * decided by one predicate and cannot drift — the bug that left `/app/audit` hidden in the menu and reachable
 * by URL.
 *
 * This replaces the previous per-route mix of `OrgModuleBoundary`, `InternalOnly` and
 * `AuthorizationBoundary`: the organization module allow-list, the user-type tenancy check and the RBAC
 * requirement are now three axes of one resolver instead of three components stacked by hand — and they apply
 * to every route, not just the handful that happened to be wrapped.
 *
 * Unguarded by design: `profile` and `settings` (every authenticated user owns their own account), and the
 * redirects.
 */
export const DashboardNavigation = () => {
  return (
    <Routes>
      {/*
        The card statement lives OUTSIDE DashboardLayout on purpose. ColocaAppShell nests page content in
        `h-svh overflow-hidden` boxes, and Chrome cannot paginate content whose ancestors clip the viewport —
        it prints the visible slice and stops. Still inside PrivateRoute, so auth is unaffected.
      */}
      <Route
        path="cards/:id/statement"
        element={
          <RequireAccess to="/app/cards/:id/statement">
            <CardStatementPage />
          </RequireAccess>
        }
      />

      {/* Layout wrapper que contiene TopBar + Sidebar */}
      <Route element={<DashboardLayout />}>
        {/* "home" section */}
        <Route
          path="wallets/*"
          element={
            <RequireAccess to="/app/wallets">
              <WalletsNavigation />
            </RequireAccess>
          }
        />
        <Route
          path="accounts-wallets"
          element={
            <RequireAccess to="/app/accounts-wallets">
              <AccountsWalletsPage />
            </RequireAccess>
          }
        />
        {/* Marco A — org onboarding (KYB); part of the "home" section (Wallets banner links here). */}
        <Route
          path="onboarding/*"
          element={
            <RequireAccess to="/app/onboarding">
              <OnboardingVerificationPage />
            </RequireAccess>
          }
        />
        {/* Payins lives in the "home" section — the Wallets page deep-links here (Cargar). */}
        <Route
          path="payins/*"
          element={
            <RequireAccess to="/app/payins">
              <PayinsNavigation />
            </RequireAccess>
          }
        />
        {/* Dispersions ("Payouts") lives in the "home" section — the Wallets page deep-links here (Enviar). */}
        <Route
          path="dispersions/*"
          element={
            <RequireAccess to="/app/dispersions">
              <DispersionsNavigation />
            </RequireAccess>
          }
        />
        {/* Pay a scanned Bre-B QR from the COP wallet — mobile-first; the PWA shortcut lands here. */}
        <Route
          path="breb-qr-pay/*"
          element={
            <RequireAccess to="/app/breb-qr-pay">
              <BrebQrPayNavigation />
            </RequireAccess>
          }
        />
        {/* Spreadsheet payouts for one corridor: upload, review, 2FA-bound approval, live progress. */}
        <Route
          path="payout-batches/*"
          element={
            <RequireAccess to="/app/payout-batches">
              <PayoutBatchesNavigation />
            </RequireAccess>
          }
        />
        <Route
          path="payments-activity/*"
          element={
            <RequireAccess to="/app/payments-activity">
              <PaymentsActivityNavigation />
            </RequireAccess>
          }
        />
        {/* Pass-through OTC conversions. One mounted route, three surfaces (form, history, one order), so
            the group guard admits on ANY of them and each sub-route carries its own `RequireAccess` — the
            `cards/*` pairing. Nothing here touches a Coloca wallet or the ledger. */}
        <Route
          path="crypto-swaps/*"
          element={
            <RequireRouteAccess path="crypto-swaps/*">
              <CryptoSwapsNavigation />
            </RequireRouteAccess>
          }
        />

        <Route
          path="organizations/*"
          element={
            <RequireAccess to="/app/organizations">
              <OrganizationsNavigation />
            </RequireAccess>
          }
        />
        <Route
          path="wallet-top-ups/*"
          element={
            <RequireAccess to="/app/wallet-top-ups">
              <WalletTopUpsNavigation />
            </RequireAccess>
          }
        />
        <Route
          path="card-wallets"
          element={
            <RequireAccess to="/app/card-wallets">
              <CardWalletsPage />
            </RequireAccess>
          }
        />

        {/* "operations" section */}
        <Route
          path="confirmations/*"
          element={
            <RequireAccess to="/app/confirmations">
              <ConfirmationsNavigation />
            </RequireAccess>
          }
        />
        <Route
          path="payout-requests/*"
          element={
            <RequireAccess to="/app/payout-requests">
              <PaymentsNavigation />
            </RequireAccess>
          }
        />
        {/* Ops rescue queue for runtime payouts stuck in CREATED (failed auto-dispatch). */}
        <Route
          path="payout-rescue/*"
          element={
            <RequireAccess to="/app/payout-rescue">
              <PayoutRescueNavigation />
            </RequireAccess>
          }
        />
        <Route
          path="swaps/*"
          element={
            <RequireAccess to="/app/swaps">
              <SwapsNavigation />
            </RequireAccess>
          }
        />

        {/* "analytics" section */}
        <Route
          path="audit/*"
          element={
            <RequireAccess to="/app/audit">
              <AuditoriaNavigation />
            </RequireAccess>
          }
        />
        {/* The sidebar shows this item under "analytics" — the registry entry says the same, so the
            route and the menu agree by construction. */}
        <Route
          path="accounts-wallets-tracking"
          element={
            <RequireAccess to="/app/accounts-wallets-tracking">
              <AccountsWalletsTrackingPage />
            </RequireAccess>
          }
        />
        <Route
          path="metrics/*"
          element={
            <RequireAccess to="/app/metrics">
              <Suspense fallback={<ColocaSkeleton className="m-2 h-40 w-full max-w-[1400px] rounded-2xl" />}>
                <MetricsNavigation />
              </Suspense>
            </RequireAccess>
          }
        />
        {/* Double-entry ledger of the payments runtime. Its registry entry carries both axes the backend
            declares on `GET /internal/payments/ledger/entries` — payments:read + userTypes:[INTERNAL] — so
            one guard covers what used to be `InternalOnly` plus a hand-written permission check. The
            backend 401/403, latched into NoAccessView, remains the real boundary. */}
        <Route
          path="payments-ledger/*"
          element={
            <RequireAccess to="/app/payments-ledger">
              <PaymentsLedgerNavigation />
            </RequireAccess>
          }
        />
        {/* Organization-verification gate, staff side: read an organization's KYC/KYB and waive its
            identity-change cooldown. Registry entry mirrors the waiver endpoint (identities:write + INTERNAL). */}
        <Route
          path="account-verification/*"
          element={
            <RequireAccess to="/app/account-verification">
              <AccountVerificationAdminPage />
            </RequireAccess>
          }
        />

        {/* "cards" section */}
        {/* Wildcard route serving three surfaces with three different requirements — `RequireRouteAccess`
            admits on ANY of them, and `CardsNavigation` guards each sub-route against its own entry. */}
        <Route
          path="cards/*"
          element={
            <RequireRouteAccess path="cards/*">
              <CardsNavigation />
            </RequireRouteAccess>
          }
        />

        {/* "management" section */}
        <Route
          path="routes-countries"
          element={
            <RequireAccess to="/app/routes-countries">
              <RoutesCountriesPage />
            </RequireAccess>
          }
        />
        {/* Five surfaces on one mounted route. They happen to share `payments:read` + INTERNAL today, so
            ANY and ALL coincide; using the group guard keeps that true if one of them ever diverges. */}
        <Route
          path="payments-config/*"
          element={
            <RequireRouteAccess path="payments-config/*">
              <PaymentsConfigNavigation />
            </RequireRouteAccess>
          }
        />
        <Route
          path="quoter/*"
          element={
            <RequireAccess to="/app/quoter">
              <QuoterNavigation />
            </RequireAccess>
          }
        />
        <Route
          path="quoter-coloca/*"
          element={
            <RequireAccess to="/app/quoter-coloca">
              <QuoterColocaNavigation />
            </RequireAccess>
          }
        />
        <Route
          path="users/*"
          element={
            <RequireAccess to="/app/users">
              <UsersNavigation />
            </RequireAccess>
          }
        />
        {/* Three surfaces on one mounted route, each with its OWN permission (roles / policies / api_key).
            The group guard admits on any of them; `AccessNavigation` then gates each sub-route with
            `RequireAccess`, so entering through one surface never grants the other two. */}
        <Route
          path="access/*"
          element={
            <RequireRouteAccess path="access/*">
              <AccessNavigation />
            </RequireRouteAccess>
          }
        />
        <Route
          path="clients/*"
          element={
            <RequireAccess to="/app/clients">
              <ClientsNavigation />
            </RequireAccess>
          }
        />
        <Route
          path="news"
          element={
            <RequireAccess to="/app/news">
              <NewsPage />
            </RequireAccess>
          }
        />

        {/* always accessible — the user's own account and the redirects */}
        <Route path="profile/*" element={<ProfileNavigation />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="dashboard" element={<DashboardLandingRedirect />} />
        <Route path="payments/*" element={<Navigate to="/app/payout-requests" replace />} />
        <Route path="/*" element={<DashboardLandingRedirect />} />
      </Route>
    </Routes>
  );
};
